import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';

import type { ArticleFilter } from 'bundles/unified-seo-content-common/__generated__/baseContentfulTypes';
import type {
  ArticleCardFieldsFragment,
  ArticleFilterQuery,
  ArticleFilterQueryVariables,
} from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import { ITEMS_PER_CATEGORY_PAGE } from 'bundles/unified-seo-content-common/constants';
import { articleFilterQuery } from 'bundles/unified-seo-content-common/queries/SeoContentHubPageQuery';
import { queryErrorHandler } from 'bundles/unified-seo-content-common/utils';

type InnerProps = {
  filters: ArticleFilter;
  articlesLimit?: number;
  articlesSkip?: number;
  preview?: boolean;
  skip?: boolean;
};

export type PageDataProps = {
  articles?: Array<ArticleCardFieldsFragment | null>;
  totalNumberOfArticles?: number;
  isSeoContentDataLoading?: boolean;
  error?: ApolloError;
};

const useArticleFilterQuery = ({
  filters,
  articlesLimit = ITEMS_PER_CATEGORY_PAGE,
  articlesSkip = 0,
  preview = false,
  skip = false,
}: InnerProps): PageDataProps => {
  const { data, loading, error } = useQuery<ArticleFilterQuery, ArticleFilterQueryVariables>(articleFilterQuery, {
    variables: { filters, articlesLimit, articlesSkip, preview },
    context: { clientName: preview ? 'contentfulPreviewGql' : 'contentfulGql' },
    errorPolicy: 'all',
    skip,
    onError: queryErrorHandler({ extraMessage: 'Error fetching seo content category-level hub page data' }),
  });

  const articles = data?.articleCollection?.items;
  const totalNumberOfArticles = data?.articleCollection?.total;

  return {
    articles,
    totalNumberOfArticles,
    isSeoContentDataLoading: loading,
    error,
  };
};

export default useArticleFilterQuery;
